// Academy content model. Courses group lessons (Attio Academy style: a
// catalog of course cards, each with an ordered lesson list and per-lesson
// pages). Lessons are plain data files under ./lessons/<courseId>/ and are
// auto-discovered by ./index.ts.

export type AcademyGate =
  /** Requires the Investigations add-on seat. */
  | "investigations"
  /** Requires the Footage analysis (video) add-on seat. */
  | "video"
  /** Only officers who belong to an agency. */
  | "agency"
  /** Agency administrators (the /agency/dashboard). */
  | "admin"
  /** Paying customers (agency members or an active personal subscription). */
  | "subscriber";

export interface Course {
  id: string;
  order: number;
  title: string;
  /** One line under the title on the card. */
  blurb: string;
  /** Heroicon name key used by the UI (see components/courseIcons.tsx). */
  icon:
    | "rocket"
    | "microphone"
    | "pencil"
    | "sparkles"
    | "document-duplicate"
    | "folder"
    | "magnifying-glass"
    | "video-camera"
    | "shield-check"
    | "cog"
    | "building-office";
  gate?: AcademyGate;
}

/** A block of lesson body copy. Kept deliberately small: no markdown parser. */
export type LessonBlock =
  | { type: "paragraph"; text: string }
  | { type: "heading"; text: string }
  /** Numbered how-to steps. Each item may name the exact UI label in **bold**. */
  | { type: "steps"; items: string[] }
  | { type: "bullets"; items: string[] }
  | { type: "callout"; tone: "tip" | "note" | "warning"; title?: string; text: string };

export interface LessonSample {
  /** File name under /academy/samples (mp3, wav, pdf…). */
  file: string;
  title: string;
  /** What the file is (one sentence). */
  description: string;
  /** Rough length, e.g. "1 min 11 s" or "2 pages". */
  length: string;
  /** What the officer should see after using it, so they can verify. */
  whatToExpect: string[];
}

export interface LessonDefinition {
  /** kebab-case, unique across all courses; also the URL segment. */
  id: string;
  courseId: string;
  order: number;
  /** Short, as it reads in the navigator ("Review the draft"). */
  title: string;
  /** Optional second line that says what it covers ("Suggestions, score, placeholders"). */
  subtitle?: string;
  /** One or two sentences shown on the course list and the lesson header. */
  summary: string;
  /** "What you will learn" (2–4 bullets). */
  learn: string[];
  /** Approximate time to complete, in minutes. */
  minutes: number;
  /** ISO date the lesson (or the feature) shipped; drives "New" badges. */
  releasedAt: string;
  body: LessonBlock[];
  /** Scene id (scenes/<id>.ts) for the living player; omitted = text-only. */
  sceneId?: string;
  /** Tour id (tutorials/tours/<id>.ts) for "Start the walkthrough". */
  tourId?: string;
  sample?: LessonSample;
  gate?: AcademyGate;
  /** Lessons worth reading next (ids). */
  related?: string[];
  /** Web only / iOS only hints shown as a chip. */
  platform?: "web" | "ios";
}

/** Inline **bold** runs in lesson copy, rendered by the UI. */
export const BOLD_RUN = /\*\*(.+?)\*\*/g;
